import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ImageFissionAspectSizeField from '@/pages/ipe/workbench/components/ImageFissionAspectSizeField';
import {
  DEFAULT_FISSION_ASPECT_PARAMS,
  IMAGE_FISSION_VARIATION_COUNT_OPTIONS,
} from '@/pages/ipe/workbench/constants/imageFissionStudioConfig';
import useStudioSidebarDockLeft from '@/pages/ipe/workbench/hooks/useStudioSidebarDockLeft';
import { useHistoryPanelTransition } from '@/pages/ipe/workbench/hooks/useHistoryPanelTransition';
import { resolveLockedTitlePresetScope } from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import '@/styles/ipe/workbench/modals/Text2ImgPromptCaseFormModal.css';
import '@/styles/ipe/workbench/Img2ImgPage.css';
import '@/styles/ipe/workbench/ImageFissionPage.css';

function emptyForm(scope = 'personal', defaults = {}) {
  const base = { ...DEFAULT_FISSION_ASPECT_PARAMS };
  return {
    name: String(defaults.name || ''),
    aspectRatio: String(defaults.aspectRatio || base.aspectRatio),
    outputWidth: Number(defaults.outputWidth ?? base.outputWidth) || base.outputWidth,
    outputHeight: Number(defaults.outputHeight ?? base.outputHeight) || base.outputHeight,
    patternOnly: defaults.patternOnly === true,
    variationCount: IMAGE_FISSION_VARIATION_COUNT_OPTIONS.includes(Number(defaults.variationCount))
      ? Number(defaults.variationCount)
      : 1,
    caseScope: scope === 'team' ? 'team' : 'personal',
  };
}

function formFromCase(item, fallbackScope = 'personal') {
  if (!item) return emptyForm(fallbackScope);
  return emptyForm(fallbackScope, item);
}

export function validateImageFissionPromptCaseForm(form, t) {
  const tr = typeof t === 'function' ? t : (key, opts) => (opts?.defaultValue ?? key);
  const name = String(form?.name || '').trim();
  if (!name) return tr('promptCase.errNameRequired');
  const ratio = String(form?.aspectRatio || '').trim();
  if (!ratio) return tr('promptCase.errRatioSizeRequired');
  if (ratio !== 'original') {
    const w = Math.floor(Number(form?.outputWidth) || 0);
    const h = Math.floor(Number(form?.outputHeight) || 0);
    if (w < 1 || h < 1) return tr('promptCase.errOutputSize');
  }
  if (!IMAGE_FISSION_VARIATION_COUNT_OPTIONS.includes(Number(form?.variationCount))) {
    return tr('promptCase.errCountRequired');
  }
  return null;
}

export function buildImageFissionPromptCasePayload(form) {
  const ratio = String(form?.aspectRatio || '2:3').trim();
  const isOriginal = ratio === 'original';
  return {
    name: String(form?.name || '').trim(),
    aspectRatio: ratio,
    outputWidth: isOriginal ? 0 : Math.max(1, Math.floor(Number(form?.outputWidth) || 0)),
    outputHeight: isOriginal ? 0 : Math.max(1, Math.floor(Number(form?.outputHeight) || 0)),
    patternOnly: form?.patternOnly === true,
    variationCount: Number(form?.variationCount) || 1,
    caseScope: form?.caseScope === 'team' ? 'team' : 'personal',
  };
}

/**
 * AI 图片裂变参数模板 · 新建 / 编辑
 */
export default function ImageFissionPromptCaseFormModal({
  isOpen,
  mode = 'create',
  /** 可选：工作台顶栏模式（不传则自动跟随顶栏）。弹窗内不可切换归属 */
  workspaceMode,
  caseItem = null,
  defaults = null,
  canAccessTeamPresets = false,
  saving = false,
  onClose,
  onSubmit,
  /** 居中弹窗：工作流节点参数面板内使用（功能页默认仍贴左侧参数栏滑入） */
  centered = false,
}) {
  const isEdit = mode === 'edit';
  const { t } = useTranslation('workbench');
  const {
    mounted,
    exiting,
    requestClose,
    handlePanelAnimationEnd,
  } = useHistoryPanelTransition(isOpen, onClose, {
    baseClassName: 't2i-case-form-modal-layer',
    deferContent: false,
    closeNotifiesParent: true,
  });
  const dockLeft = useStudioSidebarDockLeft(mounted && !centered);
  const workspaceModeFromHook = useIpeWorkspaceMode();
  const lockedScope = resolveLockedTitlePresetScope(
    workspaceMode || workspaceModeFromHook,
    canAccessTeamPresets,
  );
  const [form, setForm] = useState(() => emptyForm(lockedScope, defaults || {}));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(formFromCase(caseItem, lockedScope));
    } else {
      setForm(emptyForm(lockedScope, defaults || {}));
    }
    setError('');
  }, [isOpen, isEdit, caseItem, lockedScope, defaults]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, saving, requestClose]);

  const canSubmit = useMemo(() => {
    return Boolean(String(form.name || '').trim()) && !saving;
  }, [form.name, saving]);

  if (!mounted) return null;

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (error) setError('');
  };

  const handleAspectChange = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const msg = validateImageFissionPromptCaseForm(form, t);
    if (msg) {
      setError(msg);
      return;
    }
    const payload = buildImageFissionPromptCasePayload(form);
    await onSubmit?.(payload, { mode, caseItem });
  };

  const safeClose = () => {
    if (!saving) requestClose();
  };

  return createPortal(
    <div
      className={`funding-modal funding-modal--top t2i-case-form-modal-layer${centered ? ' ipe-case-modal--centered' : ''}${exiting ? ' is-closing' : ' is-opening'}`}
      style={{ '--t2i-case-library-dock-left': `${dockLeft}px` }}
    >
      <div
        className="funding-modal__backdrop"
        role="presentation"
        onClick={safeClose}
      />
      <div
        className="funding-modal__panel funding-modal__panel--unified funding-modal__panel--t2i-case-form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fission-case-form-title"
        onAnimationEnd={handlePanelAnimationEnd}
      >
        <header className="funding-modal__header t2i-case-form__header">
          <div className="t2i-case-form__header-text">
            <h3 id="fission-case-form-title">{isEdit ? t('promptCase.editTemplate') : t('promptCase.newTemplate')}</h3>
            <p className="t2i-case-form__subtitle">
              {isEdit
                ? t('promptCase.syncHint')
                : t('promptCase.fissionSubtitle')}
            </p>
          </div>
        </header>

        <form className="funding-modal__body t2i-case-form__body" onSubmit={handleSubmit}>
          <section className="t2i-case-form__section">
            <label className="t2i-case-form__label" htmlFor="fission-case-name">
              {t('promptCase.name')}<span className="t2i-case-form__req" style={{ color: '#f87171' }}>*</span>
            </label>
            <input
              id="fission-case-name"
              type="text"
              className="t2i-case-form__input"
              value={form.name}
              maxLength={128}
              disabled={saving}
              onChange={(e) => setField('name', e.target.value)}
              placeholder={t('promptCase.fissionNamePh')}
            />
          </section>

          <section className="t2i-case-form__section img-fission-page__aspect-section">
            <span className="t2i-case-form__label">{t('promptCase.ratioSize')}</span>
            <ImageFissionAspectSizeField
              value={{
                aspectRatio: form.aspectRatio,
                outputWidth: form.outputWidth,
                outputHeight: form.outputHeight,
              }}
              onChange={handleAspectChange}
              readOnly={saving}
            />
          </section>

          <section className="t2i-case-form__section">
            <div className="t2i-case-form__label-row">
              <span className="t2i-case-form__label">{t('promptCase.patternOnly')}</span>
              <label className="img2img-studio__toggle">
                <input
                  type="checkbox"
                  checked={form.patternOnly === true}
                  onChange={(e) => setField('patternOnly', e.target.checked)}
                  disabled={saving}
                  aria-label={t('promptCase.patternOnly')}
                />
                <span className="img2img-studio__toggle-track" aria-hidden="true" />
              </label>
            </div>
            <p className="t2i-case-form__hint">{t('promptCase.patternOnlyHint')}</p>
          </section>

          <section className="t2i-case-form__section">
            <div className="t2i-case-form__label-row">
              <span className="t2i-case-form__label">{t('promptCase.generateCount')}</span>
              <span className="t2i-case-form__meta">{t('promptCase.generateCountRange')}</span>
            </div>
            <div className="img2img-studio__qty-row" role="group" aria-label={t('promptCase.generateCount')}>
              {IMAGE_FISSION_VARIATION_COUNT_OPTIONS.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`img2img-studio__qty-btn${form.variationCount === n ? ' is-active' : ''}`}
                  onClick={() => setField('variationCount', n)}
                  disabled={saving}
                  aria-pressed={form.variationCount === n}
                >
                  {n}
                </button>
              ))}
            </div>
          </section>

          {error ? (
            <p className="t2i-case-form__error" role="alert">{error}</p>
          ) : null}

          <footer className="t2i-case-form__footer">
            <button
              type="button"
              className="funding-button funding-button--ghost t2i-case-form__btn"
              onClick={onClose}
              disabled={saving}
            >
              {t('promptCase.cancel')}
            </button>
            <button
              type="submit"
              className="funding-button funding-button--accent t2i-case-form__btn t2i-case-form__btn--primary"
              disabled={!canSubmit}
            >
              {saving
                ? t('promptCase.saving')
                : (isEdit ? t('promptCase.saveChanges') : t('promptCase.createTemplate'))}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body,
  );
}
